<template>
    <div>
        <div class="card border-0 shadow-sm">
            <div class="card-body p-4">
                <h5 class="mb-3">License Management</h5>

                <div v-if="loading" class="text-center py-5">
                    <app-overlay-loader/>
                </div>

                <div v-else>
                    <!-- License Status -->
                    <div class="d-flex align-items-center mb-4 p-3 rounded" :class="statusBgClass">
                        <div class="mr-3">
                            <app-icon :name="statusIcon" :class="'size-40 ' + statusColor"/>
                        </div>
                        <div>
                            <h6 class="mb-1">{{ license.product || 'Transcenda Portal' }} — {{ tierLabel }}</h6>
                            <small class="text-muted">
                                Status: <strong :class="statusColor">{{ license.status || 'Unknown' }}</strong>
                                <span v-if="license.expires"> | Expires: {{ license.expires }}</span>
                            </small>
                        </div>
                    </div>

                    <!-- License Key Display -->
                    <div class="mb-4">
                        <label class="text-muted font-size-90 mb-1">License Key</label>
                        <div class="d-flex align-items-center">
                            <code class="p-2 bg-light rounded mr-2 flex-grow-1">{{ maskedKey }}</code>
                            <button class="btn btn-sm btn-outline-primary" @click="validateLicense" :disabled="validating">
                                <span v-if="validating">Validating...</span>
                                <span v-else>Validate</span>
                            </button>
                        </div>
                    </div>

                    <!-- Features -->
                    <div v-if="license.features && license.features.length" class="mb-4">
                        <label class="text-muted font-size-90 mb-2">Included Features</label>
                        <div class="d-flex flex-wrap">
                            <span v-for="f in license.features" :key="f"
                                  class="badge badge-light border mr-1 mb-1 px-2 py-1">
                                {{ f }}
                            </span>
                        </div>
                    </div>

                    <!-- Limits -->
                    <div v-if="license.max_users || license.max_branches" class="mb-4">
                        <div class="row">
                            <div class="col-6" v-if="license.max_users">
                                <label class="text-muted font-size-90">Max Users</label>
                                <p class="font-weight-bold">{{ license.max_users == -1 ? 'Unlimited' : license.max_users }}</p>
                            </div>
                            <div class="col-6" v-if="license.max_branches">
                                <label class="text-muted font-size-90">Max Branches</label>
                                <p class="font-weight-bold">{{ license.max_branches == -1 ? 'Unlimited' : license.max_branches }}</p>
                            </div>
                        </div>
                    </div>

                    <hr/>

                    <!-- Change License Key -->
                    <div class="mt-4">
                        <h6 class="mb-2">Activate New License Key</h6>
                        <div class="d-flex">
                            <input type="text" v-model="newKey" class="form-control mr-2"
                                   placeholder="TPU-XXXX-XXXX-XXXX-XXXX"/>
                            <button class="btn btn-primary" @click="activateLicense" :disabled="!newKey || activating">
                                <span v-if="activating">Activating...</span>
                                <span v-else>Activate</span>
                            </button>
                        </div>
                        <small v-if="activateError" class="text-danger mt-1 d-block">{{ activateError }}</small>
                        <small v-if="activateSuccess" class="text-success mt-1 d-block">License activated successfully!</small>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {axiosGet, axiosPost} from "../../../Helpers/AxiosHelper";

export default {
    name: "LicenseManagement",
    props: ["props"],
    data() {
        return {
            loading: true,
            validating: false,
            activating: false,
            license: {},
            newKey: '',
            activateError: '',
            activateSuccess: false,
        }
    },
    computed: {
        maskedKey() {
            const k = this.license.key || '';
            if (!k || k.length < 10) return k || 'No key configured';
            const parts = k.split('-');
            if (parts.length === 5) return `${parts[0]}-${parts[1]}-****-****-${parts[4]}`;
            return k.substring(0, 8) + '****' + k.substring(k.length - 4);
        },
        tierLabel() {
            const t = (this.license.tier || 'unknown').toLowerCase();
            return t.charAt(0).toUpperCase() + t.slice(1);
        },
        statusIcon() {
            return this.license.valid ? 'check-circle' : 'alert-circle';
        },
        statusColor() {
            return this.license.valid ? 'text-success' : 'text-danger';
        },
        statusBgClass() {
            return this.license.valid ? 'bg-light' : 'bg-light';
        }
    },
    created() {
        this.fetchStatus();
    },
    methods: {
        fetchStatus() {
            this.loading = true;
            axiosGet('license/status')
                .then(({data}) => {
                    this.license = data;
                })
                .catch(() => {
                    this.license = {valid: false, status: 'Error fetching license'};
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        validateLicense() {
            this.validating = true;
            axiosPost('license/validate', {})
                .then(({data}) => {
                    this.license = data;
                    this.$toastr.s('License validated successfully');
                })
                .catch(({response}) => {
                    this.$toastr.e(response?.data?.error || 'Validation failed');
                })
                .finally(() => {
                    this.validating = false;
                });
        },
        activateLicense() {
            this.activating = true;
            this.activateError = '';
            this.activateSuccess = false;
            axiosPost('license/activate', {key: this.newKey})
                .then(({data}) => {
                    this.license = data;
                    this.activateSuccess = true;
                    this.newKey = '';
                    this.$toastr.s('License activated!');
                })
                .catch(({response}) => {
                    this.activateError = response?.data?.error || 'Activation failed';
                })
                .finally(() => {
                    this.activating = false;
                });
        }
    }
}
</script>
